import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar,
} from 'recharts';

const fmtUGX = (n) => {
  if (!n) return 'UGX 0';
  if (n >= 1e9) return `UGX ${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `UGX ${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `UGX ${(n / 1e3).toFixed(1)}K`;
  return `UGX ${Math.round(n).toLocaleString()}`;
};

const fmtNum = (n) => (n || 0).toLocaleString();

const Card = ({ children, className = '' }) => (
  <div className={`bg-[#0d1426] border border-white/10 rounded-2xl p-5 ${className}`}>
    {children}
  </div>
);

const StatCard = ({ label, value, sub, color = 'text-cyan-400' }) => (
  <Card>
    <div className="text-white/50 text-xs uppercase tracking-wider mb-1">{label}</div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    {sub && <div className="text-white/40 text-xs mt-1">{sub}</div>}
  </Card>
);

const Badge = ({ status }) => {
  const map = {
    open: 'bg-amber-500/20 text-amber-400',
    in_progress: 'bg-blue-500/20 text-blue-400',
    resolved: 'bg-green-500/20 text-green-400',
    closed: 'bg-white/10 text-white/40',
    pending_review: 'bg-amber-500/20 text-amber-400',
    approved: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400',
    critical: 'bg-red-500/20 text-red-400',
    high: 'bg-orange-500/20 text-orange-400',
    medium: 'bg-amber-500/20 text-amber-400',
    low: 'bg-blue-500/20 text-blue-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-white/10 text-white/50'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const TABS = ['Overview', 'Customers', 'Finance', 'Compliance', 'Marketing', 'Reports'];

export default function SuperAdmin() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({});

  const fetchTab = useCallback(async (tab) => {
    const endpoints = [
      '/superadmin/overview',
      '/superadmin/customers',
      '/superadmin/finance',
      '/superadmin/compliance',
      '/superadmin/marketing',
      null,
    ];
    const url = endpoints[tab];
    if (!url) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(url);
      setData(prev => ({ ...prev, [tab]: res.data || res }));
    } catch (e) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTab(activeTab); }, [activeTab]);

  const handleTab = (i) => { setActiveTab(i); setError(''); };

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white">
      <div className="max-w-screen-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Super Admin Center</h1>
          <p className="text-white/40 text-sm mt-1">Go-live administration & business intelligence</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/10 overflow-x-auto">
          {TABS.map((t, i) => (
            <button
              key={t}
              onClick={() => handleTab(i)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === i
                  ? 'border-cyan-400 text-white'
                  : 'border-transparent text-white/50 hover:text-white/80'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-5 text-red-400 text-sm">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-white/40 text-sm mb-4">
            <div className="w-3 h-3 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            Loading...
          </div>
        )}

        {activeTab === 0 && <OverviewTab data={data[0]} />}
        {activeTab === 1 && <CustomersTab />}
        {activeTab === 2 && <FinanceTab data={data[2]} />}
        {activeTab === 3 && <ComplianceTab data={data[3]} />}
        {activeTab === 4 && <MarketingTab data={data[4]} refresh={() => fetchTab(4)} />}
        {activeTab === 5 && <ReportsTab />}
      </div>
    </div>
  );
}

// ── TAB 1: OVERVIEW ────────────────────────────────────────────
function OverviewTab({ data }) {
  if (!data) return <div className="text-white/40 text-sm">Loading overview...</div>;
  const { users, financialToday, financialMonth, wallets, operations, countries, health } = data;

  return (
    <div className="space-y-6">
      {/* Users */}
      <div>
        <h2 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Users</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard label="Total Users" value={fmtNum(users?.total)} />
          <StatCard label="Active Today" value={fmtNum(users?.activeToday)} color="text-green-400" />
          <StatCard label="New Today" value={fmtNum(users?.newToday)} color="text-cyan-400" />
          <StatCard label="New This Week" value={fmtNum(users?.newThisWeek)} />
          <StatCard label="New This Month" value={fmtNum(users?.newThisMonth)} />
          <StatCard label="KYC Pending" value={fmtNum(users?.kyc?.pending)} color="text-amber-400" />
          <StatCard label="Suspended" value={fmtNum(users?.suspended)} color="text-red-400" />
        </div>
      </div>

      {/* Financial Today */}
      <div>
        <h2 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Financial — Today</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Deposits Today" value={fmtUGX(financialToday?.depositsAmount)} sub={`${fmtNum(financialToday?.depositsCount)} txns`} color="text-green-400" />
          <StatCard label="Withdrawals Today" value={fmtUGX(financialToday?.withdrawalsAmount)} sub={`${fmtNum(financialToday?.withdrawalsCount)} txns`} color="text-red-400" />
          <StatCard label="Open Tickets" value={fmtNum(operations?.openTickets)} color="text-amber-400" />
          <StatCard label="Open AML Alerts" value={fmtNum(operations?.openAlerts)} color="text-red-400" />
        </div>
      </div>

      {/* Financial Month */}
      <div>
        <h2 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Financial — This Month</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Deposits (Month)" value={fmtUGX(financialMonth?.depositsAmount)} sub={`${fmtNum(financialMonth?.depositsCount)} txns`} color="text-green-400" />
          <StatCard label="Withdrawals (Month)" value={fmtUGX(financialMonth?.withdrawalsAmount)} sub={`${fmtNum(financialMonth?.withdrawalsCount)} txns`} color="text-orange-400" />
          <StatCard label="Investment AUM" value={fmtUGX(financialMonth?.investmentTotal)} color="text-purple-400" />
          <StatCard label="KYC Approved" value={fmtNum(users?.kyc?.approved)} color="text-green-400" />
        </div>
      </div>

      {/* Wallet Totals + Country Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Platform Wallet Balances</h3>
          <div className="space-y-2">
            {(wallets || []).map(w => (
              <div key={w._id} className="flex justify-between items-center bg-white/5 rounded-lg px-4 py-2.5">
                <span className="text-white/70 text-sm font-medium">{w._id || 'UGX'}</span>
                <div className="text-right">
                  <div className="text-cyan-400 font-bold">{w._id === 'UGX' ? fmtUGX(w.total) : `${w._id} ${(w.total || 0).toFixed(4)}`}</div>
                  <div className="text-white/30 text-xs">{fmtNum(w.wallets)} wallets</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Users by Country</h3>
          <div className="space-y-2">
            {(countries || []).map((c, i) => (
              <div key={c._id || i} className="flex justify-between items-center bg-white/5 rounded-lg px-4 py-2.5">
                <span className="text-white/70 text-sm">{c._id || 'Unknown'}</span>
                <span className="text-cyan-400 font-bold">{fmtNum(c.count)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* System Health */}
      <Card>
        <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">System Health</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-white/40 text-xs">Uptime</div>
            <div className="text-white font-semibold mt-1">
              {health ? `${Math.floor((health.uptime || 0) / 3600)}h ${Math.floor(((health.uptime || 0) % 3600) / 60)}m` : '—'}
            </div>
          </div>
          <div>
            <div className="text-white/40 text-xs">Memory</div>
            <div className="text-white font-semibold mt-1">{health ? `${health.memoryMB} / ${health.memoryTotalMB} MB` : '—'}</div>
          </div>
          <div>
            <div className="text-white/40 text-xs">Node.js</div>
            <div className="text-white font-semibold mt-1">{health?.nodeVersion || '—'}</div>
          </div>
          <div>
            <div className="text-white/40 text-xs">Process ID</div>
            <div className="text-white font-semibold mt-1">{health?.pid || '—'}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── TAB 2: CUSTOMERS ───────────────────────────────────────────
function CustomersTab() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterKyc, setFilterKyc] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [notifForm, setNotifForm] = useState({ title: '', body: '' });
  const [freezeReason, setFreezeReason] = useState('');

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get('/superadmin/customers', { params: {
        search: search || undefined,
        status: filterStatus || undefined,
        kyc: filterKyc || undefined,
        role: filterRole || undefined,
        page: p,
        limit: 20,
      }});
      setResult(res);
      setPage(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterKyc, filterRole]);

  useEffect(() => { load(1); }, []);

  const loadDetail = async (id) => {
    try {
      const res = await api.get(`/superadmin/customers/${id}`);
      setDetail(res.data);
      setSelected(id);
      setActionMsg('');
    } catch (e) { console.error(e); }
  };

  const doFreeze = async (id) => {
    try {
      await api.post(`/superadmin/customers/${id}/freeze`, { reason: freezeReason || 'Frozen by super admin' });
      setActionMsg('Account frozen successfully');
      loadDetail(id);
      setFreezeReason('');
    } catch (e) { setActionMsg(e.message || 'Error'); }
  };

  const doUnfreeze = async (id) => {
    try {
      await api.post(`/superadmin/customers/${id}/unfreeze`);
      setActionMsg('Account unfrozen successfully');
      loadDetail(id);
    } catch (e) { setActionMsg(e.message || 'Error'); }
  };

  const doReset2fa = async (id) => {
    try {
      await api.delete(`/superadmin/customers/${id}/2fa`);
      setActionMsg('2FA reset successfully');
      loadDetail(id);
    } catch (e) { setActionMsg(e.message || 'Error'); }
  };

  const doNotify = async (id) => {
    if (!notifForm.title || !notifForm.body) { setActionMsg('Title and body required'); return; }
    try {
      await api.post(`/superadmin/customers/${id}/notify`, { ...notifForm, type: 'system' });
      setActionMsg('Notification sent');
      setNotifForm({ title: '', body: '' });
    } catch (e) { setActionMsg(e.message || 'Error'); }
  };

  const users = result?.data || [];

  return (
    <div className="space-y-5">
      {/* Search + filters */}
      <Card>
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(1)}
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-500"
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          <select value={filterKyc} onChange={e => setFilterKyc(e.target.value)} className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none">
            <option value="">All KYC</option>
            <option value="none">No KYC</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none">
            <option value="">All Roles</option>
            <option value="client">Client</option>
            <option value="admin">Admin</option>
            <option value="agent">Agent</option>
            <option value="merchant">Merchant</option>
          </select>
          <button onClick={() => load(1)} className="bg-cyan-500 hover:bg-cyan-400 text-[#0a0f1e] font-semibold px-5 py-2.5 rounded-xl text-sm">
            Search
          </button>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-x-auto p-0">
        {loading ? (
          <div className="p-6 text-white/40 text-sm">Loading...</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-white/50 px-5 py-3 font-medium">Name</th>
                <th className="text-left text-white/50 px-5 py-3 font-medium">Email</th>
                <th className="text-left text-white/50 px-5 py-3 font-medium">Phone</th>
                <th className="text-left text-white/50 px-5 py-3 font-medium">Role</th>
                <th className="text-left text-white/50 px-5 py-3 font-medium">KYC</th>
                <th className="text-left text-white/50 px-5 py-3 font-medium">Status</th>
                <th className="text-left text-white/50 px-5 py-3 font-medium">Joined</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u._id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="px-5 py-3 text-white font-medium">{u.name}</td>
                  <td className="px-5 py-3 text-white/60">{u.email}</td>
                  <td className="px-5 py-3 text-white/60">{u.phone}</td>
                  <td className="px-5 py-3"><Badge status={u.role} /></td>
                  <td className="px-5 py-3"><Badge status={u.kyc?.status || 'none'} /></td>
                  <td className="px-5 py-3">
                    {u.isSuspended
                      ? <span className="text-red-400 text-xs font-medium">Suspended</span>
                      : <span className="text-green-400 text-xs font-medium">Active</span>}
                  </td>
                  <td className="px-5 py-3 text-white/40 text-xs">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => loadDetail(u._id)} className="text-cyan-400 hover:text-cyan-300 text-xs">View</button>
                  </td>
                </tr>
              ))}
              {!users.length && (
                <tr><td colSpan={8} className="px-5 py-6 text-white/30 text-center">No users found</td></tr>
              )}
            </tbody>
          </table>
        )}
        {result && result.pages > 1 && (
          <div className="flex gap-2 items-center justify-center p-4 border-t border-white/10">
            <button onClick={() => load(page - 1)} disabled={page <= 1} className="px-3 py-1.5 text-xs bg-white/5 rounded-lg disabled:opacity-30">Prev</button>
            <span className="text-white/40 text-xs">Page {page} of {result.pages}</span>
            <button onClick={() => load(page + 1)} disabled={page >= result.pages} className="px-3 py-1.5 text-xs bg-white/5 rounded-lg disabled:opacity-30">Next</button>
          </div>
        )}
      </Card>

      {/* Customer Detail Modal */}
      {selected && detail && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-[#0d1426] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex justify-between items-start">
              <div>
                <h2 className="text-white font-bold text-lg">{detail.user?.name}</h2>
                <div className="text-white/40 text-sm">{detail.user?.email} · {detail.user?.phone}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white text-xl">✕</button>
            </div>

            <div className="p-5 space-y-5">
              {/* Status info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="text-white/40 text-xs mb-1">Role</div>
                  <div className="text-white">{detail.user?.role}</div>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="text-white/40 text-xs mb-1">KYC</div>
                  <Badge status={detail.user?.kyc?.status || 'none'} />
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="text-white/40 text-xs mb-1">2FA</div>
                  <div className={detail.user?.twoFactorEnabled ? 'text-green-400' : 'text-white/40'}>
                    {detail.user?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="text-white/40 text-xs mb-1">Email Verified</div>
                  <div className={detail.user?.emailVerified ? 'text-green-400' : 'text-amber-400'}>
                    {detail.user?.emailVerified ? 'Verified' : 'Unverified'}
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="text-white/40 text-xs mb-1">Last Login</div>
                  <div className="text-white text-xs">{detail.user?.lastLogin ? new Date(detail.user.lastLogin).toLocaleString() : '—'}</div>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="text-white/40 text-xs mb-1">Last IP</div>
                  <div className="text-white text-xs">{detail.user?.lastLoginIp || '—'}</div>
                </div>
              </div>

              {/* Wallets */}
              {detail.wallets?.length > 0 && (
                <div>
                  <div className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">Wallets</div>
                  <div className="space-y-1.5">
                    {detail.wallets.map(w => (
                      <div key={w._id} className="flex justify-between bg-white/5 rounded-xl px-4 py-2.5">
                        <span className="text-white/60 text-sm">{w.currency}</span>
                        <span className="text-cyan-400 font-bold text-sm">
                          {w.currency === 'UGX' ? fmtUGX(w.balance) : `${(w.balance || 0).toFixed(6)} ${w.currency}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Transactions */}
              {detail.recentTxns?.length > 0 && (
                <div>
                  <div className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">Recent Transactions</div>
                  <div className="space-y-1">
                    {detail.recentTxns.slice(0, 5).map(t => (
                      <div key={t._id} className="flex justify-between bg-white/5 rounded-lg px-3 py-2 text-xs">
                        <span className="text-white/60">{t.type?.replace(/_/g, ' ')}</span>
                        <span className="text-white/40">{new Date(t.createdAt).toLocaleDateString()}</span>
                        <span className={t.type === 'deposit' ? 'text-green-400' : 'text-white/70'}>
                          {t.currency === 'UGX' ? fmtUGX(t.amount) : `${(t.amount || 0).toFixed(4)} ${t.currency}`}
                        </span>
                        <Badge status={t.status} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="border-t border-white/10 pt-4 space-y-4">
                <div className="text-white/50 text-xs font-semibold uppercase tracking-wider">Admin Actions</div>

                {/* Freeze / Unfreeze */}
                <div className="flex flex-col gap-2">
                  <input
                    type="text"
                    placeholder="Freeze reason (optional)"
                    value={freezeReason}
                    onChange={e => setFreezeReason(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none"
                  />
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => doFreeze(detail.user._id)}
                      className="bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 px-4 py-2 rounded-xl text-sm font-medium"
                    >
                      Freeze Account
                    </button>
                    <button
                      onClick={() => doUnfreeze(detail.user._id)}
                      className="bg-green-500/20 border border-green-500/30 text-green-400 hover:bg-green-500/30 px-4 py-2 rounded-xl text-sm font-medium"
                    >
                      Unfreeze Account
                    </button>
                    <button
                      onClick={() => doReset2fa(detail.user._id)}
                      className="bg-amber-500/20 border border-amber-500/30 text-amber-400 hover:bg-amber-500/30 px-4 py-2 rounded-xl text-sm font-medium"
                    >
                      Reset 2FA
                    </button>
                  </div>
                </div>

                {/* Send Notification */}
                <div className="space-y-2">
                  <div className="text-white/40 text-xs">Send In-App Notification</div>
                  <input
                    type="text"
                    placeholder="Title"
                    value={notifForm.title}
                    onChange={e => setNotifForm(p => ({ ...p, title: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Message body"
                    value={notifForm.body}
                    onChange={e => setNotifForm(p => ({ ...p, body: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none"
                  />
                  <button onClick={() => doNotify(detail.user._id)} className="bg-cyan-500 hover:bg-cyan-400 text-[#0a0f1e] font-semibold px-4 py-2 rounded-xl text-sm">
                    Send Notification
                  </button>
                </div>

                {actionMsg && (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 text-cyan-400 text-sm">
                    {actionMsg}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TAB 3: FINANCE ─────────────────────────────────────────────
function FinanceTab({ data }) {
  if (!data) return <div className="text-white/40 text-sm">Loading finance data...</div>;
  const { today, thisMonth, exchange, investmentAUM, walletBalances, pendingWithdrawals, dailyBreakdown } = data;

  const doExport = async (type) => {
    try {
      const blob = await api.get('/superadmin/finance/export', { params: { type }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `dwi-report-${type}-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { console.error(e); }
  };

  const monthGrowth = thisMonth?.lastMonthDeposits > 0
    ? (((thisMonth?.deposits - thisMonth?.lastMonthDeposits) / thisMonth?.lastMonthDeposits) * 100).toFixed(1)
    : null;

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Deposits Today" value={fmtUGX(today?.deposits)} sub={`${fmtNum(today?.depositsCount)} txns`} color="text-green-400" />
        <StatCard label="Withdrawals Today" value={fmtUGX(today?.withdrawals)} sub={`${fmtNum(today?.withdrawalsCount)} txns`} color="text-red-400" />
        <StatCard
          label="Deposits This Month"
          value={fmtUGX(thisMonth?.deposits)}
          sub={monthGrowth ? `${monthGrowth > 0 ? '+' : ''}${monthGrowth}% vs last month` : `${fmtNum(thisMonth?.depositsCount)} txns`}
          color="text-cyan-400"
        />
        <StatCard label="Pending Withdrawals" value={fmtUGX(pendingWithdrawals?.total)} sub={`${fmtNum(pendingWithdrawals?.count)} pending`} color="text-amber-400" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Exchange Volume" value={fmtUGX(exchange?.volume)} sub={`${fmtNum(exchange?.count)} orders`} />
        <StatCard label="Investment AUM" value={fmtUGX(investmentAUM)} color="text-purple-400" />
        <StatCard label="Withdrawals (Month)" value={fmtUGX(thisMonth?.withdrawals)} sub={`${fmtNum(thisMonth?.withdrawalsCount)} txns`} color="text-orange-400" />
        <StatCard label="Last Month Deposits" value={fmtUGX(thisMonth?.lastMonthDeposits)} />
      </div>

      {/* Platform wallet balances */}
      <Card>
        <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Platform Float (All Wallets)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(walletBalances || []).map(w => (
            <div key={w._id} className="bg-white/5 rounded-xl p-3 text-center">
              <div className="text-white/40 text-xs mb-1">{w._id || 'UGX'}</div>
              <div className="text-cyan-400 font-bold">
                {w._id === 'UGX' ? fmtUGX(w.balance) : `${(w.balance || 0).toFixed(4)}`}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Daily Chart */}
      {dailyBreakdown?.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">Daily Deposits — This Month</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={dailyBreakdown} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="depGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="_id" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} tickFormatter={v => v.split('-').slice(1).join('/')} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
              <Tooltip
                contentStyle={{ background: '#0d1426', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }}
                formatter={v => fmtUGX(v)}
              />
              <Area type="monotone" dataKey="deposits" stroke="#06b6d4" fill="url(#depGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Export */}
      <Card>
        <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">Export Reports</h3>
        <div className="flex flex-wrap gap-3">
          {['daily', 'weekly', 'monthly', 'annual'].map(type => (
            <button key={type} onClick={() => doExport(type)} className="bg-white/5 border border-white/10 hover:border-cyan-500/50 text-white px-5 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2">
              <span>📊</span>
              {type.charAt(0).toUpperCase() + type.slice(1)} Report (xlsx)
            </button>
          ))}
        </div>
        <p className="text-white/30 text-xs mt-3">Downloads a transaction export for the selected period. Maximum 5,000 rows.</p>
      </Card>
    </div>
  );
}

// ── TAB 4: COMPLIANCE ──────────────────────────────────────────
function ComplianceTab({ data }) {
  if (!data) return <div className="text-white/40 text-sm">Loading compliance data...</div>;
  const { kyc, aml, transactions, users } = data;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="KYC Pending" value={fmtNum(kyc?.pending)} color="text-amber-400" />
        <StatCard label="KYC Rejected" value={fmtNum(kyc?.rejected)} color="text-red-400" />
        <StatCard label="AML Alerts Open" value={fmtNum(aml?.open)} color={aml?.open > 0 ? 'text-red-400' : 'text-green-400'} />
        <StatCard label="AML Critical" value={fmtNum(aml?.critical)} color={aml?.critical > 0 ? 'text-red-400' : 'text-green-400'} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatCard label="Suspended Accounts" value={fmtNum(users?.suspended)} color="text-red-400" />
        <StatCard label="High-Value Txns (≥1M UGX)" value={fmtNum(transactions?.highValue?.length)} sub="last 20 shown" />
      </div>

      {/* AML Alerts */}
      <Card>
        <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Open AML Alerts</h3>
        {aml?.recentAlerts?.length ? (
          <div className="space-y-2">
            {aml.recentAlerts.map(a => (
              <div key={a._id} className="bg-white/5 rounded-xl p-4">
                <div className="flex justify-between items-start mb-1">
                  <div className="text-white text-sm font-medium">{a.alertType}</div>
                  <div className="flex gap-2">
                    <Badge status={a.severity} />
                    <Badge status={a.status} />
                  </div>
                </div>
                <div className="text-white/50 text-xs">{a.description}</div>
                {a.userId && (
                  <div className="text-white/30 text-xs mt-1">
                    {a.userId.name} · {a.userId.email}
                  </div>
                )}
                <div className="text-white/20 text-xs mt-1">{new Date(a.createdAt).toLocaleString()}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-white/30 text-sm">No open AML alerts</div>
        )}
      </Card>

      {/* KYC Queue */}
      <Card>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider">KYC Pending Review</h3>
          <a href="/admin-kyc" className="text-cyan-400 text-xs hover:text-cyan-300">Open KYC Queue →</a>
        </div>
        {kyc?.recentPending?.length ? (
          <div className="space-y-2">
            {kyc.recentPending.map(k => (
              <div key={k._id} className="flex justify-between items-center bg-white/5 rounded-xl px-4 py-2.5 text-sm">
                <div>
                  <div className="text-white font-medium">{k.userId?.name || 'Unknown'}</div>
                  <div className="text-white/40 text-xs">{k.userId?.email}</div>
                </div>
                <div className="text-right">
                  <Badge status={k.tier} />
                  <div className="text-white/30 text-xs mt-1">{new Date(k.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-white/30 text-sm">No pending KYC submissions</div>
        )}
      </Card>

      {/* High-Value Transactions */}
      <Card className="overflow-x-auto p-0">
        <div className="px-5 py-3 border-b border-white/10">
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider">High-Value Transactions (≥ 1M UGX)</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-white/40 px-5 py-3 font-medium">Date</th>
              <th className="text-left text-white/40 px-5 py-3 font-medium">Customer</th>
              <th className="text-left text-white/40 px-5 py-3 font-medium">Type</th>
              <th className="text-left text-white/40 px-5 py-3 font-medium">Amount</th>
              <th className="text-left text-white/40 px-5 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {(transactions?.highValue || []).map(t => (
              <tr key={t._id} className="border-b border-white/5">
                <td className="px-5 py-3 text-white/40 text-xs">{new Date(t.createdAt).toLocaleDateString()}</td>
                <td className="px-5 py-3">
                  <div className="text-white text-sm">{t.user?.name || 'N/A'}</div>
                  <div className="text-white/30 text-xs">{t.user?.phone}</div>
                </td>
                <td className="px-5 py-3 text-white/60">{t.type?.replace(/_/g, ' ')}</td>
                <td className="px-5 py-3 text-amber-400 font-bold">{fmtUGX(t.amount)}</td>
                <td className="px-5 py-3"><Badge status={t.status} /></td>
              </tr>
            ))}
            {!(transactions?.highValue?.length) && (
              <tr><td colSpan={5} className="px-5 py-6 text-white/30 text-center">No high-value transactions</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ── TAB 5: MARKETING ───────────────────────────────────────────
function MarketingTab({ data, refresh }) {
  const [notifForm, setNotifForm] = useState({ title: '', body: '', targetRole: 'client' });
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const sendBulk = async () => {
    if (!notifForm.title || !notifForm.body) { setMsg('Title and body required'); return; }
    setSending(true);
    setMsg('');
    try {
      const res = await api.post('/superadmin/marketing/notify', notifForm);
      setMsg(res.message || 'Sent');
      setNotifForm({ title: '', body: '', targetRole: 'client' });
    } catch (e) {
      setMsg(e.message || 'Error sending');
    } finally {
      setSending(false);
    }
  };

  const { referrals } = data || {};

  return (
    <div className="space-y-6">
      {/* Referral Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Total Referred Users" value={fmtNum(referrals?.total)} color="text-cyan-400" />
        <StatCard label="Total Referral Earnings" value={fmtUGX(referrals?.totalEarnings)} color="text-green-400" />
        <StatCard label="Active Referrers" value={fmtNum(referrals?.topReferrers?.length)} sub="with at least 1 referral" />
      </div>

      {/* Top Referrers */}
      <Card>
        <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Top Referrers</h3>
        {referrals?.topReferrers?.length ? (
          <div className="space-y-2">
            {referrals.topReferrers.map((u, i) => (
              <div key={u._id} className="flex justify-between items-center bg-white/5 rounded-xl px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-xs flex items-center justify-center font-bold">{i + 1}</div>
                  <div>
                    <div className="text-white text-sm font-medium">{u.name}</div>
                    <div className="text-white/40 text-xs">{u.email} · Code: {u.referral?.code || '—'}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-cyan-400 font-bold">{fmtNum(u.referral?.level1Count)} refs</div>
                  <div className="text-white/40 text-xs">{fmtUGX(u.referral?.totalEarned)} earned</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-white/30 text-sm">No referral data yet</div>
        )}
      </Card>

      {/* Push Notifications */}
      <Card>
        <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">Send Bulk In-App Notification</h3>
        <div className="space-y-3 max-w-lg">
          <div>
            <label className="text-white/50 text-xs mb-1 block">Target Audience</label>
            <select
              value={notifForm.targetRole}
              onChange={e => setNotifForm(p => ({ ...p, targetRole: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none"
            >
              <option value="client">All Clients</option>
              <option value="agent">All Agents</option>
              <option value="merchant">All Merchants</option>
              <option value="admin">Admins Only</option>
            </select>
          </div>
          <div>
            <label className="text-white/50 text-xs mb-1 block">Title</label>
            <input
              type="text"
              placeholder="Notification title"
              value={notifForm.title}
              onChange={e => setNotifForm(p => ({ ...p, title: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-500"
            />
          </div>
          <div>
            <label className="text-white/50 text-xs mb-1 block">Message</label>
            <textarea
              placeholder="Notification message..."
              value={notifForm.body}
              onChange={e => setNotifForm(p => ({ ...p, body: e.target.value }))}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-500 resize-none"
            />
          </div>
          <button
            onClick={sendBulk}
            disabled={sending}
            className="bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-[#0a0f1e] font-semibold px-6 py-2.5 rounded-xl text-sm"
          >
            {sending ? 'Sending...' : 'Send Bulk Notification'}
          </button>
          {msg && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 text-cyan-400 text-sm">{msg}</div>
          )}
        </div>
        <p className="text-white/30 text-xs mt-3">Sends an in-app notification to all non-suspended users of the selected role.</p>
      </Card>

      {/* CMS Banners */}
      {data?.banners?.length > 0 && (
        <Card>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider">Active Banners</h3>
            <a href="/cms-admin" className="text-cyan-400 text-xs hover:text-cyan-300">Manage in CMS →</a>
          </div>
          <div className="space-y-2">
            {data.banners.map(b => (
              <div key={b._id} className="flex justify-between items-center bg-white/5 rounded-xl px-4 py-2.5 text-sm">
                <span className="text-white">{b.title || b.name || '—'}</span>
                <span className={b.isActive ? 'text-green-400 text-xs' : 'text-white/30 text-xs'}>{b.isActive ? 'Active' : 'Inactive'}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── TAB 6: REPORTS ─────────────────────────────────────────────
function ReportsTab() {
  const [reportType, setReportType] = useState('monthly');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const download = async () => {
    setLoading(true);
    setMsg('');
    try {
      const params = { type: reportType };
      if (from) params.from = from;
      if (to) params.to = to;

      const blob = await api.get('/superadmin/finance/export', { params, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const date = new Date().toISOString().split('T')[0];
      a.download = `dwi-report-${reportType}-${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      setMsg('Download started');
    } catch (e) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const TYPES = [
    { key: 'daily', label: 'Daily Report', desc: "Today's transactions" },
    { key: 'weekly', label: 'Weekly Report', desc: 'Last 7 days' },
    { key: 'monthly', label: 'Monthly Report', desc: 'Current calendar month' },
    { key: 'annual', label: 'Annual Report', desc: 'Current year to date' },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">Generate Report</h3>
        <div className="space-y-4 max-w-lg">
          <div>
            <label className="text-white/50 text-xs mb-2 block">Report Type</label>
            <div className="grid grid-cols-2 gap-2">
              {TYPES.map(t => (
                <button
                  key={t.key}
                  onClick={() => setReportType(t.key)}
                  className={`p-3 rounded-xl border text-left transition-colors ${
                    reportType === t.key
                      ? 'border-cyan-400 bg-cyan-400/10'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                >
                  <div className="text-white text-sm font-medium">{t.label}</div>
                  <div className="text-white/40 text-xs">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-white/50 text-xs mb-2 block">Custom Date Range (overrides type)</label>
            <div className="flex gap-3">
              <div className="flex-1">
                <div className="text-white/30 text-xs mb-1">From</div>
                <input
                  type="date"
                  value={from}
                  onChange={e => setFrom(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-500"
                />
              </div>
              <div className="flex-1">
                <div className="text-white/30 text-xs mb-1">To</div>
                <input
                  type="date"
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-cyan-500"
                />
              </div>
            </div>
            {(from || to) && (
              <button onClick={() => { setFrom(''); setTo(''); }} className="text-xs text-white/30 hover:text-white/60 mt-1">
                Clear custom dates
              </button>
            )}
          </div>

          <button
            onClick={download}
            disabled={loading}
            className="w-full bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-[#0a0f1e] font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-[#0a0f1e] border-t-transparent rounded-full animate-spin" />
                Generating...
              </>
            ) : (
              <>📊 Download Excel Report</>
            )}
          </button>

          {msg && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 text-cyan-400 text-sm">{msg}</div>
          )}
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Report Contents</h3>
        <div className="space-y-2 text-sm text-white/60">
          <p>Each report includes two Excel sheets:</p>
          <ul className="space-y-1 ml-4">
            <li>• <span className="text-white">Transactions</span> — Date, Time, Reference, Type, Status, Amount, Currency, Customer Name, Email, Phone, Note</li>
            <li>• <span className="text-white">Summary</span> — Total deposits, withdrawals, net flow, exchange order counts, generation timestamp</li>
          </ul>
          <p className="text-white/30 text-xs mt-2">Maximum 5,000 rows per export. For larger datasets, use a custom date range to split the period.</p>
        </div>
      </Card>
    </div>
  );
}
